import React, { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { dataMappingAPI } from '../services/api';

interface MedicationForm {
  medId: string;
  dataSourceId: string;
  investigationalCode: string;
  chemicalName: string;
  inn: string;
  genericName: string;
  abbreviation1: string;
  abbreviation2: string;
  internalNames: string[];
}

interface MedicationRow extends MedicationForm {
  id: number;
}

const MedicationMapping: React.FC = () => {
  const [items, setItems] = useState<MedicationRow[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<MedicationForm>({
    medId: '',
    dataSourceId: '',
    investigationalCode: '',
    chemicalName: '',
    inn: '',
    genericName: '',
    abbreviation1: '',
    abbreviation2: '',
    internalNames: Array(10).fill(''),
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const rows = await dataMappingAPI.getMedications();
        const mapped: MedicationRow[] = rows.map((row: any) => ({
          id: row.id,
          medId: row.Med_ID || '',
          dataSourceId: row.Data_Sources_ID || '',
          investigationalCode: row.Investigational_Code || '',
          chemicalName: row.Chemical_Name || '',
          inn: row.International_Nonproprietary_Name || '',
          genericName: row.Generic_Name || '',
          abbreviation1: row.Abbreviation1 || '',
          abbreviation2: row.Abbreviation2 || '',
          internalNames: [
            row.Internal_Name1 || '',
            row.Internal_Name2 || '',
            row.Internal_Name3 || '',
            row.Internal_Name4 || '',
            row.Internal_Name5 || '',
            row.Internal_Name6 || '',
            row.Internal_Name7 || '',
            row.Internal_Name8 || '',
            row.Internal_Name9 || '',
            row.Internal_Name10 || '',
          ],
        }));
        setItems(mapped);
      } catch (error) {
        console.error('Error loading medications for mapping:', error);
        toast.error('Failed to load medications from Data Finder system.');
      }
    };

    loadData();
  }, []);

  const handleChange = (field: keyof MedicationForm, value: string | string[]) => {
    setForm(prev => ({ ...prev, [field]: value as any }));
  };

  const handleInternalNameChange = (index: number, value: string) => {
    setForm(prev => {
      const updated = [...prev.internalNames];
      updated[index] = value;
      return { ...prev, internalNames: updated };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Wire to Data Finder medication API / mapping service
    const nextId = items.length > 0 ? Math.max(...items.map(i => i.id)) + 1 : 1;
    const newItem: MedicationRow = { id: nextId, ...form };
    setItems(prev => [...prev, newItem]);
    toast.success('Medication mapping captured (not yet persisted).');
    setForm({
      medId: '',
      dataSourceId: '',
      investigationalCode: '',
      chemicalName: '',
      inn: '',
      genericName: '',
      abbreviation1: '',
      abbreviation2: '',
      internalNames: Array(10).fill(''),
    });
    setShowForm(false);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            Medication Mapping
          </h1>
          <p className="text-sm text-gray-600">
            View all medication mappings and add new definitions that align with the Source Data Discovery Medication table.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm(prev => !prev)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--accent)] text-white text-sm font-medium shadow-sm hover:opacity-90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Add Medication</span>
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
            Medications
          </h2>
          <span className="text-xs text-gray-500">
            {items.length} record{items.length === 1 ? '' : 's'}
          </span>
        </div>
        {items.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-gray-500">
            No medications defined yet. Click <span className="font-medium">Add Medication</span> to create your first mapping.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Med_ID</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Data_Sources_ID</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Generic Name</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">INN</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Abbrev1</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Abbrev2</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs text-gray-800">{item.medId}</td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-800">{item.dataSourceId}</td>
                    <td className="px-4 py-2 text-gray-800">{item.genericName}</td>
                    <td className="px-4 py-2 text-gray-800">{item.inn}</td>
                    <td className="px-4 py-2 text-gray-800">{item.abbreviation1}</td>
                    <td className="px-4 py-2 text-gray-800">{item.abbreviation2}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="space-y-6 bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Med_ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.medId}
              onChange={e => handleChange('medId', e.target.value)}
              placeholder="M0001"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
            />
            <p className="mt-1 text-xs text-gray-500">
              Unique Medication Standard Code in database (M + 4 digits, e.g., M0001).
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data_Sources_ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.dataSourceId}
              onChange={e => handleChange('dataSourceId', e.target.value)}
              placeholder="D001"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
            />
            <p className="mt-1 text-xs text-gray-500">
              Reference to data provider (must match an existing Data_Sources_Provider ID, e.g., D001).
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Investigational_Code
            </label>
            <input
              type="text"
              value={form.investigationalCode}
              onChange={e => handleChange('investigationalCode', e.target.value)}
              placeholder="INV-2025-001"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
            />
            <p className="mt1 text-xs text-gray-500">
              Study-specific drug code (alphanumeric, e.g., INV-2025-001).
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Chemical_Name
            </label>
            <input
              type="text"
              value={form.chemicalName}
              onChange={e => handleChange('chemicalName', e.target.value)}
              placeholder="IUPAC name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              International_Nonproprietary_Name (INN)
            </label>
            <input
              type="text"
              value={form.inn}
              onChange={e => handleChange('inn', e.target.value)}
              placeholder="Official WHO INN"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Generic_Name
            </label>
            <input
              type="text"
              value={form.genericName}
              onChange={e => handleChange('genericName', e.target.value)}
              placeholder="Rifampin"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Abbreviation1
            </label>
            <input
              type="text"
              value={form.abbreviation1}
              onChange={e => handleChange('abbreviation1', e.target.value)}
              placeholder="RIF"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
            />
            <p className="mt-1 text-xs text-gray-500">
              Full drug name abbreviation as used in publications (3 letters, e.g., RIF, INH).
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Abbreviation2
            </label>
            <input
              type="text"
              value={form.abbreviation2}
              onChange={e => handleChange('abbreviation2', e.target.value)}
              placeholder="R"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
            />
            <p className="mt-1 text-xs text-gray-500">
              Drug abbreviation used in combination regimens (1–2 letters, e.g., R, Pa).
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Internal_Name1–10
          </label>
          <p className="text-xs text-gray-600 mb-3">
            Internal naming conventions used within your organization (synonyms, lab codes, or
            study-specific identifiers). Up to 10 values can be captured.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {form.internalNames.map((value, index) => (
              <div key={index}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Internal_Name{index + 1}
                </label>
                <input
                  type="text"
                  value={value}
                  onChange={e => handleInternalNameChange(index, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setShowForm(false)}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="inline-flex items-center px-4 py-2 rounded-lg bg-[var(--accent)] text-white text-sm font-medium hover:opacity-90 transition-colors"
          >
            Save Mapping
          </button>
        </div>
      </form>
      )}

      <div className="mt-6 text-xs text-gray-500">
        This section is designed to support consistent medication naming and linking between
        publications and the Data Finder Medication table. Future iterations will integrate with
        the Data Finder API to validate and store these mappings.
      </div>
    </div>
  );
};

export default MedicationMapping;

